<template>
  <div :key="`publication-view-${publicationId}`" class="view-workspacemodel">
    <!-- BREADCRUMPS -->
    <div class="breadcrump-trail view-workspacemodel__breadcrumps">
      <router-link
        class="breadcrump-trail__item"
        :to="{ name: 'public-models' }"
      >
        <lucide-icon name="chevron-left" class="breadcrump-trail__arrow" />
        {{ $t("labels.exampleModels") }}
      </router-link>
    </div>

    <error-page-content
      v-if="error"
      :title="error.message"
      :status="error.status"
      :error-code="error.code"
      class="view-publicmodel__panel"
    >
      <template #backlink>
        <router-link :to="{ name: 'public-models' }">
          {{ $t("labels.backToModels") }}
        </router-link>
      </template>
    </error-page-content>
    <p v-else-if="!publicModel && (loading || isLoading)">...</p>
    <model-detail
      v-else-if="publicModel"
      :model="publicModel"
      :model-workspace="null"
      :editor-route="{
        name: 'public-model',
        params: {
          publicationId: publicModel.id
        }
      }"
      :show-modified-at="false"
    >
      <template #main>
        <h4 class="mb-2">{{ $t("labels.author") }}</h4>
        <span>{{ publicModel?.author }}</span>
      </template>
    </model-detail>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";

import ModelDetail from "@manager/workspacemodels/ModelDetail.vue";
import ErrorPageContent from "@core/errors/ErrorPageContent.vue";

import { usePublicModels } from "@/api/publicModels.store";

type LoadError = Error & {
  status?: number;
  code?: string;
};

const props = defineProps<{
  publicationId?: string;
}>();

const { publicModel, isLoading, getPublicModelInfo } = usePublicModels(props);

const loading = ref(true);
const error = ref<LoadError | null>(null);

onMounted(() => {
  loadPublication();
});

async function loadPublication() {
  if (!props.publicationId) {
    return;
  }

  loading.value = true;
  try {
    return await getPublicModelInfo(props.publicationId);
  } catch (e) {
    console.error(e);
    error.value = e as LoadError;
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss">
.view-publicmodel__panel {
  background-color: var(--color-background-deep);
  border-radius: 5px;
  box-shadow: 0 2px 5px var(--color-shadow-1);
  padding: 2rem;
  padding-top: 1rem;
}
</style>
